/**
 * Message Type Constants
 *
 * Defines all message types used in data channel communication
 * between the web client and robots.
 */

// ============== Status Message Topics (robot -> web) ==============

export const STATUS_TOPICS = {
  // Standard periodic snapshot { robot_info, robot_state, mission_info } published by the
  // robot over the data channel (see features/robot-teleop/utils/robotStatus.js).
  // The flat `robot_status` message of older robots is deprecated.
  ROBOT_STATUS: 'robot.status',
};

// ============== Robot Status Messages ==============

export const MESSAGE_TYPES = {
  // Robot status
  ROBOT_STATUS: STATUS_TOPICS.ROBOT_STATUS,
  MISSION_STATUS: 'mission_status',

  // Control commands
  CMD_VEL: 'cmd_vel',
  JOY: 'joy',
  KEYBOARD: 'keyboard',

  // Robot-specific
  TWIST: 'twist',
  POSE: 'pose',
  ODOMETRY: 'odometry',

  // System messages
  HEARTBEAT: 'heartbeat',
  LOG: 'log',
  ERROR: 'error',
};

// ============== Data Channel Names ==============

export const DATA_CHANNELS = {
  CHAT: 'chat',
  CONTROL: 'control',
  TELEMETRY: 'telemetry',
  VIDEO: 'video',
  AUDIO: 'audio',
};

// ============== Robot Status Fields ==============

// Field names of the standard `robot.status` value (STATUS_TOPICS.ROBOT_STATUS)
export const ROBOT_STATUS_FIELDS = {
  // Top-level sections
  ROBOT_INFO: 'robot_info',
  ROBOT_STATE: 'robot_state',
  MISSION_INFO: 'mission_info',

  // robot_info
  ROBOT_ID: 'robot_id',
  SUB_ID: 'sub_id',
  ANGLE_UNIT: 'angle_unit',
  ROBOT_TYPE: 'robot_type',       // { category, model }
  CAPABILITY: 'capability',       // { common: [], custom: [] }

  // robot_state
  BASE_STATE: 'base_state',
  POSE: 'pose',                   // { x, y, z, qx, qy, qz, qw }
  VELOCITY: 'velocity',           // { linear: [x, y, z], angular: [x, y, z], frame }
  BATTERY_STATE: 'battery_state', // { percentage, is_charging, voltage, current, temperature }
  JOINT_STATE: 'joint_state',     // { names, positions, velocities, efforts }
  RAW_STATUS: 'raw_status',       // vendor specific flags
  EXTENSION: 'extension',         // vendor specific extras (e.g. ros_twist)

  // mission_info
  MISSION_STATE: 'mission_state',
  MISSION_NAME: 'mission_name',
  CURRENT_TASK: 'current_task',
  AVAILABLE_MISSIONS: 'available_missions', // [{ name, description }]
};

// ============== Command Message Topics ==============

export const COMMAND_TOPICS = {
  CMD_VEL: '/cmd_vel',
  JOY: '/joy',
  KEYBOARD: '/keyboard',
  TELEOP_TWIST: '/teleop/twist',
  TELEOP_JOY: '/teleop/joy',
  NLP_INSTRUCTION: '/nlp_instruction',
};

// ============== Message Priorities ==============

export const MESSAGE_PRIORITIES = {
  CRITICAL: 100,  // Emergency stop, errors
  HIGH: 75,       // Control commands
  NORMAL: 50,     // Status updates
  LOW: 25,        // Logs, statistics
  BACKGROUND: 0,  // Heartbeats
};

// ============== Message Validation Rules ==============

export const VALIDATION_RULES = {
  MAX_MESSAGE_SIZE: 64 * 1024, // 64KB
  MAX_TOPIC_LENGTH: 256,
  MAX_PAYLOAD_SIZE: 32 * 1024, // 32KB
  MIN_THROTTLE_INTERVAL: 10,   // 10ms minimum throttle
  MAX_RETRY_ATTEMPTS: 3,
};

// Export all as single object
export default {
  MESSAGE_TYPES,
  DATA_CHANNELS,
  ROBOT_STATUS_FIELDS,
  COMMAND_TOPICS,
  STATUS_TOPICS,
  MESSAGE_PRIORITIES,
  VALIDATION_RULES,
};
